if (!customElements.get('dtc-search')) {
  customElements.define('dtc-search', class extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-dtc-search-data]');
      try {
        this.codes = dataEl ? JSON.parse(dataEl.textContent) : [];
      } catch (e) {
        this.codes = [];
      }
      this.codesByHandle = new Map(this.codes.map((c) => [c.h, c]));
      this.basePath = this.dataset.basePath || window.location.pathname;

      this.input = this.querySelector('[data-dtc-search-input]');
      this.suggestions = this.querySelector('[data-dtc-suggestions]');
      this.listView = this.querySelector('[data-dtc-list-view]');
      this.selectedSuggestion = -1;

      if (this.input) {
        this.input.addEventListener('input', () => this.updateSuggestions());
        this.input.addEventListener('focus', () => this.updateSuggestions());
        this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      }
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.hideSuggestions();
      });
      window.addEventListener('popstate', () => this.syncFromUrl());
      this.syncFromUrl();
    }

    syncFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const q = (params.get('q') || '').trim().toLowerCase();

      // Legacy ?q=<code-handle> links (bookmarks, old shares) predate the per-code
      // pages. Send them to the real URL. Liquid cannot read the query string, so a
      // server-side 301 is not possible for this pattern.
      const legacy = q ? this.codesByHandle.get(q) : null;
      if (legacy && legacy.u) {
        window.location.replace(legacy.u);
        return;
      }

      // Anything else (category slugs, free text) stays a client-side list filter.
      this.syncFilterPills(q);
      if (q && this.input && !this.input.value) this.input.value = q;
      this.applyListFilter(q);
    }

    syncFilterPills(currentQ) {
      const pills = this.querySelectorAll('.dtc-search__filter');
      for (const pill of pills) {
        let pillQ = '';
        try {
          pillQ = (new URL(pill.href, window.location.origin).searchParams.get('q') || '').toLowerCase();
        } catch (e) {
          pillQ = '';
        }
        const isActive = pillQ === currentQ;
        pill.classList.toggle('is-active', isActive);
        pill.setAttribute('aria-selected', isActive ? 'true' : 'false');
      }
    }

    applyListFilter(q) {
      const items = Array.from(this.listView.querySelectorAll('[data-dtc-item]'));
      if (!items.length) return;

      let visible = 0;
      for (const item of items) {
        if (!q) {
          item.hidden = false;
          visible++;
          continue;
        }
        const hay = item.dataset.dtcHaystack || '';
        const match = hay.includes(q);
        item.hidden = !match;
        if (match) visible++;
      }

      const emptyEl = this.listView.querySelector('[data-dtc-empty]');
      const countEl = this.listView.querySelector('[data-dtc-count]');

      if (emptyEl) emptyEl.hidden = visible > 0;
      if (countEl && q) {
        countEl.textContent = `${visible} code${visible === 1 ? '' : 's'} found`;
      }
    }

    updateSuggestions() {
      const q = (this.input.value || '').trim().toLowerCase();
      if (!q) return this.hideSuggestions();

      const matches = [];
      for (const code of this.codes) {
        const handle = (code.h || '').toLowerCase();
        const name = (code.c || '').toLowerCase();
        const desc = (code.d || '').toLowerCase();
        if (handle.includes(q) || name.includes(q) || desc.includes(q)) {
          matches.push(code);
          if (matches.length >= 8) break;
        }
      }
      if (matches.length === 0) return this.hideSuggestions();

      this.renderSuggestions(matches);
      this.suggestions.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.selectedSuggestion = -1;
    }

    renderSuggestions(matches) {
      this.suggestions.innerHTML = '';
      matches.forEach((m, i) => {
        const li = document.createElement('li');
        li.setAttribute('role', 'option');
        li.id = `dtc-suggestion-${i}`;

        const a = document.createElement('a');
        a.className = 'dtc-search__suggestion';
        a.href = m.u || this.basePath;

        const code = document.createElement('span');
        code.className = 'dtc-search__suggestion-code';
        code.textContent = m.c;

        const desc = document.createElement('span');
        desc.className = 'dtc-search__suggestion-description';
        desc.textContent = m.d;

        a.appendChild(code);
        a.appendChild(desc);
        li.appendChild(a);
        this.suggestions.appendChild(li);
      });
    }

    onKeydown(e) {
      if (this.suggestions.hidden) return;
      const items = this.suggestions.querySelectorAll('.dtc-search__suggestion');
      if (!items.length) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.selectedSuggestion = (this.selectedSuggestion + 1) % items.length;
        this.highlightSuggestion(items);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.selectedSuggestion = this.selectedSuggestion <= 0 ? items.length - 1 : this.selectedSuggestion - 1;
        this.highlightSuggestion(items);
      } else if (e.key === 'Enter' && this.selectedSuggestion >= 0) {
        e.preventDefault();
        items[this.selectedSuggestion].click();
      } else if (e.key === 'Escape') {
        this.hideSuggestions();
        this.input.blur();
      }
    }

    highlightSuggestion(items) {
      items.forEach((item, i) => {
        const active = i === this.selectedSuggestion;
        item.classList.toggle('is-highlighted', active);
        if (active) {
          this.input.setAttribute('aria-activedescendant', item.parentElement.id);
          item.scrollIntoView({ block: 'nearest' });
        }
      });
    }

    hideSuggestions() {
      this.suggestions.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.selectedSuggestion = -1;
    }
  });
}
